import { useEffect, useRef } from "react";
import { useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { App as AntdApp } from "antd";
import { AxiosError } from "axios";

import {
  isInFlight,
  isTerminal,
  useOperationsStore,
  type TrackedOperation,
} from "@/stores/operationsStore";
import { getDatabaseProgress, getOperation } from "./api";

/** Poll cadence per in-flight operation. 5s keeps three concurrent ops at 36
 *  req/min — safely under the 60/min rate limit (3s exhausted it). */
const POLL_MS = 5000;

/** Consecutive failed polls before we stop and mark the outcome unknowable —
 *  a failed poll must not leave the op "pending" forever. */
const MAX_POLL_FAILURES = 6;

/**
 * Terminal-state cache invalidation, scoped to the operation's resource
 * family. Never `invalidateQueries()` bare — that refetches every query in
 * the app (an app-wide refetch storm) to update one list.
 */
const KIND_QUERY_KEYS: Record<TrackedOperation["kind"], readonly QueryKey[]> = {
  instance: [["compute"]],
  volume: [["storage"]],
  loadbalancer: [["lbaas"]],
  // A server snapshot lands as a Glance image; the instance itself also
  // transitions (task_state image_snapshot), so refresh both families.
  snapshot: [["images"], ["compute"]],
  cluster: [["k8s"]],
  function: [["functions"]],
  database: [["database"]],
  other: [],
};

function OperationPoller({ op }: { op: TrackedOperation }) {
  const setStatus = useOperationsStore((s) => s.setStatus);
  const { message, notification } = AntdApp.useApp();
  const queryClient = useQueryClient();

  const { data, error, dataUpdatedAt, errorUpdatedAt } = useQuery({
    // Keyed by this attempt as well as the id: an id is reused when the same
    // name is submitted again (provision-lb-{user}-{name}), and the first
    // attempt's cached "failed" would otherwise end the second one's tracking
    // at once, with the old reason, so its real outcome was never reported
    // (platform test H-2: the balancer vanished without a word).
    queryKey: ["operation", op.workflowId, op.startedAt],
    queryFn: () =>
      op.resource ? getDatabaseProgress(op.resource.id) : getOperation(op.workflowId),
    // Failures are handled below with our own counter — TanStack's built-in
    // retries would just multiply the request rate.
    retry: false,
    refetchInterval: (query) =>
      query.state.data && isTerminal(query.state.data.status) ? false : POLL_MS,
  });

  useEffect(() => {
    if (!data || data.status === op.status) return;
    setStatus(op.workflowId, data.status, data.result_id, data.error ?? null);
    if (isTerminal(data.status)) {
      // The create/delete workflow only just finished mutating the cloud, so
      // this resource family's lists are now stale. The mutation's own
      // onSuccess fired when the workflow was *dispatched* (resource not yet
      // present), so refresh again on completion — otherwise the new
      // instance/volume/etc. only appears after a manual reload.
      for (const key of KIND_QUERY_KEYS[op.kind]) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    }
    if (data.status === "completed") {
      message.success(`${op.label} — done`);
    } else if (
      data.status === "failed" ||
      data.status === "timed_out" ||
      // Stopped by the platform before it finished (T3-06): the customer who
      // started it heard nothing at all.
      data.status === "terminated"
    ) {
      notification.error({
        message: `${op.label} — failed`,
        // The backend's sanitized failure reason, when it sends one.
        description:
          data.error ??
          (data.status === "terminated"
            ? "The operation was stopped before it finished."
            : undefined),
        // Stays until dismissed: the outcome arrives minutes after the
        // request, when the customer may be looking elsewhere.
        duration: 0,
        role: "alert",
      });
    }
  }, [data, op, setStatus, message, notification, queryClient]);

  // Consecutive failed polls, counted here (T3-04): with ``retry: false``
  // TanStack Query resets its own failureCount at the start of every fetch, so
  // it read 1 every time and MAX_POLL_FAILURES never tripped.
  const failedPolls = useRef(0);
  useEffect(() => {
    if (dataUpdatedAt) failedPolls.current = 0;
  }, [dataUpdatedAt]);

  // Error path: a hard 404 (unknown/expired workflow) or repeated poll
  // failures must resolve the op — the old tracker left it "pending" forever.
  useEffect(() => {
    if (!error) return;
    failedPolls.current += 1;
    const status =
      error instanceof AxiosError ? error.response?.status : undefined;
    if (status !== 404 && failedPolls.current < MAX_POLL_FAILURES) return;
    setStatus(op.workflowId, "failed", null, "status unavailable");
    notification.error({
      message: `${op.label} — status unavailable`,
      description:
        "We could not confirm this operation's outcome. Refresh the list to check the result.",
      duration: 0,
      role: "alert",
    });
    // The operation may still have finished server-side — refresh the
    // resource family so the list shows whatever really happened.
    for (const key of KIND_QUERY_KEYS[op.kind]) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
    // errorUpdatedAt: one run per failed poll, even when the error repeats.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, errorUpdatedAt]);

  return null;
}

/**
 * Mounts an invisible poller per in-flight operation. Each poller polls the
 * operation-status endpoint, updates the store, and toasts once on completion
 * or failure — the fix for "fired a create, then silence". Rendered once in the
 * app shell.
 */
export default function OperationsTracker() {
  const operations = useOperationsStore((s) => s.operations);
  const markStale = useOperationsStore((s) => s.markStale);

  // Overall timeout sweep: anything non-terminal past 30 min flips to
  // timed_out (and its poller unmounts) instead of lingering as "pending".
  useEffect(() => {
    markStale();
    const timer = setInterval(markStale, 60_000);
    return () => clearInterval(timer);
  }, [markStale]);

  return (
    <>
      {operations.filter(isInFlight).map((op) => (
        <OperationPoller key={op.workflowId} op={op} />
      ))}
    </>
  );
}
